/**
 * 帳單管理（平台管理員）· func_code = ee_platform_billing。
 * 草稿 → 開立並通知 → 已收款 / 作廢；帶入 AI 用量；重新通知；通知紀錄；匯款帳號設定。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import FunctionPageHeader from '../../../components/FunctionPageHeader';
import EInvoicePanel from './EInvoicePanel';
import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import { getOrganizations, Organization } from '../../../services/organizationService';
import * as svc from '../../../services/platformBillingService';
import { Bill, BillItem, BillStatus, RemitConfig } from '../../../services/platformBillingService';
import * as einvSvc from '../../../services/einvoiceService';

const FUNC_CODE = 'ee_platform_billing';
const EMPTY_REMIT: RemitConfig = {
  bank_name: '', bank_code: '', branch: '', account_name: '', account_no: '',
};
const STATUSES: BillStatus[] = ['draft', 'unpaid', 'paid', 'void'];

/** 合計區一行（標籤靠左、NT$ 金額靠右）。 */
const Row: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2">NT$ {(value ?? 0).toLocaleString()}</Typography>
  </Stack>
);

const PlatformBillingPage: React.FC = () => {
  const { t } = useTranslation();
  const tt = (k: string, d: string, o?: Record<string, unknown>) => t(`platformBilling.${k}`, d, o);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showInfo, showWarning, showApiError } = useMessage();
  const [topTab, setTopTab] = useState(0);   // 0 帳單管理 / 1 電子發票
  // 電子發票設定（S5：開立確認是否提示開發票）
  const [einvEnabled, setEinvEnabled] = useState(false);

  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [remit, setRemit] = useState<RemitConfig>(EMPTY_REMIT);
  const [lineCount, setLineCount] = useState<number>(0);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);
  const [fTenant, setFTenant] = useState<number | ''>('');
  const [fStatus, setFStatus] = useState<BillStatus | ''>('');
  const [note, setNote] = useState<string | null>(null);

  // ── 共用確認對話框（取代 window.confirm·S4）────────────
  type ConfirmColor = 'primary' | 'error' | 'success';
  const [confirmState, setConfirmState] = useState<{
    open: boolean; title: string; message: string;
    confirmLabel: string; confirmColor: ConfirmColor; resolve?: (v: boolean) => void;
  }>({ open: false, title: '', message: '', confirmLabel: '', confirmColor: 'primary' });

  const askConfirm = (opts: { title: string; message: string;
    confirmLabel?: string; confirmColor?: ConfirmColor }) =>
    new Promise<boolean>(resolve => setConfirmState({
      open: true, title: opts.title, message: opts.message,
      confirmLabel: opts.confirmLabel || t('common.confirm', '確定'),
      confirmColor: opts.confirmColor || 'primary', resolve,
    }));

  const closeConfirm = (v: boolean) => {
    confirmState.resolve?.(v);
    setConfirmState(s => ({ ...s, open: false, resolve: undefined }));
  };

  const orgName = useCallback((id: number) =>
    orgs.find(o => o.id === id)?.org_name || String(id), [orgs]);

  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      setBills(await svc.listBills({
        tenant_org_id: fTenant || undefined, status: fStatus || undefined,
      }));
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [fTenant, fStatus, showApiError]);

  const loadMeta = useCallback(async () => {
    try {
      setOrgs(await getOrganizations());
      setRemit(await svc.getRemit());
      const now = new Date();
      const u = await svc.getLineUsage(now.getFullYear(), now.getMonth() + 1);
      setLineCount(u.count);
      try {
        const cfg = await einvSvc.getConfig();
        setEinvEnabled(!!cfg.enabled && cfg.complete);
      } catch { /* silent ok: 電子發票設定讀取失敗不影響帳單作業 */ }
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read')) { loadMeta(); loadBills(); loadTaxUpdates(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading]);

  // ── 匯款帳號 ──────────────────────────────────────────
  const saveRemit = async () => {
    try {
      await svc.saveRemit(remit);
      showSuccess('SYS450006');
    } catch (err) { showApiError(err); }
  };

  // ── 公司稅務資料（統編 / 公司登記名稱 / 稅籍編號·租戶 + 平台共維）────────────
  const [profOpen, setProfOpen] = useState(false);
  const [profOrg, setProfOrg] = useState<number | ''>('');
  const [prof, setProf] = useState<{ tax_id: string; invoice_title: string; tax_registration_no: string }>(
    { tax_id: '', invoice_title: '', tax_registration_no: '' });
  const [profLoaded, setProfLoaded] = useState<svc.TenantInvoiceProfile | null>(null);
  const [profBusy, setProfBusy] = useState(false);
  // C1：租戶改過、平台未看過的清單（按鈕徽章 + 下拉「（已更新）」+ 列表標記）。
  const [taxUpdates, setTaxUpdates] = useState<svc.TenantTaxProfileUpdate[]>([]);
  const updatedOrgIds = useMemo(() => new Set(taxUpdates.map(u => u.tenant_org_id)), [taxUpdates]);
  const loadTaxUpdates = useCallback(async () => {
    try { setTaxUpdates(await svc.listTenantTaxProfileUpdates()); }
    catch { /* silent ok: 徽章清單載入失敗不擋帳單作業 */ }
  }, []);
  const loadProfile = useCallback(async (orgId: number) => {
    try {
      const p = await svc.getTenantInvoiceProfile(orgId);   // 平台開啟 → 後端標記已看過（回清除前值）
      setProfLoaded(p);
      setProf({ tax_id: p.tax_id || '', invoice_title: p.invoice_title || '',
        tax_registration_no: p.tax_registration_no || '' });
      loadTaxUpdates();                                     // 看過後刷新徽章 / 列表標記
    } catch (err) { showApiError(err); }
  }, [showApiError, loadTaxUpdates]);
  const openProfile = () => {
    const first = orgs[0]?.id || '';
    setProfOrg(first);
    setProfLoaded(null);
    setProf({ tax_id: '', invoice_title: '', tax_registration_no: '' });
    setProfOpen(true);
    if (first) loadProfile(first);
  };
  const saveProfile = async () => {
    if (!profOrg) return;
    setProfBusy(true);
    try {
      const saved = await svc.saveTenantInvoiceProfile(profOrg, {
        tax_id: prof.tax_id.trim() || null, invoice_title: prof.invoice_title.trim() || null,
        tax_registration_no: prof.tax_registration_no.trim() || null,
        expected_updated_at: profLoaded?.updated_at || null });   // 樂觀鎖
      showSuccess('SYS020001', { name: tt('taxProfile', '公司稅務資料') });
      // lint-msg-allow: 代碼由後端回傳（SYS450016·統編與他租戶重複·非阻斷提示）
      if (saved.tax_id_warning) showWarning(saved.tax_id_warning);
      setProfLoaded(saved);                                 // 更新 updated_at 供下次樂觀鎖
      loadTaxUpdates();
    } catch (err) { showApiError(err); } finally { setProfBusy(false); }  // ERR450031=409 樂觀鎖衝突
  };

  // ── 帳單草稿 Dialog ───────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Bill | null>(null);
  const [draft, setDraft] = useState<svc.BillCreate>({
    tenant_org_id: 0, title: '', period_start: '', period_end: '', items: [], due_date: '',
  });

  // 合計預覽：稅基模型（費用未稅外加 5%·優惠價＝含稅總額）。後端為權威計算，此處僅草稿預覽。
  // 與後端 einvoice.tax.compute_bill_tax / suggest_promo_total 一致。
  const draftBreakdown = useMemo(() => {
    const items = draft.items;
    const subtotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const taxBefore = Math.round(subtotal * 0.05);
    const gross = subtotal + taxBefore;
    const withPromo = items.filter(i => i.promo_incl != null)
      .reduce((s, i) => s + Number(i.promo_incl), 0);
    const manualUntaxed = items.filter(i => i.promo_incl == null)
      .reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const suggested = withPromo + Math.round(manualUntaxed * 1.05);
    let promo: number | null;
    if (draft.promo_manual) promo = (draft.promo_total ?? null);
    else promo = suggested !== gross ? suggested : null;
    let total: number; let sales: number; let tax: number;
    if (promo == null) { total = gross; sales = subtotal; tax = taxBefore; }
    else { total = promo; tax = Math.round((promo * 5) / 105); sales = promo - tax; }
    return { subtotal, taxBefore, gross, suggested, promo, total, sales, tax };
  }, [draft.items, draft.promo_manual, draft.promo_total]);
  const draftTotal = draftBreakdown.total;

  const openNew = () => {
    setEditing(null);
    setDraft({ tenant_org_id: orgs[0]?.id || 0, title: '', period_start: '', period_end: '',
      items: [{ name: '', amount: 0 }], due_date: '', invoice_no: '',
      promo_total: null, promo_manual: false });
    setEditOpen(true);
  };
  const openEdit = (b: Bill) => {
    setEditing(b);
    setDraft({ tenant_org_id: b.tenant_org_id, title: b.title,
      period_start: b.period_start || '', period_end: b.period_end || '',
      items: b.items.length ? b.items : [{ name: '', amount: 0 }],
      due_date: b.due_date || '', invoice_no: b.invoice_no || '',
      promo_total: b.promo_total ?? null, promo_manual: !!b.promo_manual });
    setEditOpen(true);
  };
  const setItem = (idx: number, patch: Partial<BillItem>) =>
    setDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setDraft(d => ({ ...d, items: [...d.items, { name: '', amount: 0 }] }));
  const removeItem = (idx: number) =>
    setDraft(d => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));

  // ── 附件（v2）：每張帳單一個附件對話框（草稿可增刪·已開立只能新增·作廢唯讀）────
  const ATT_MAX = 5;
  const ATT_MAX_BYTES = 5 * 1024 * 1024;
  const ATT_ALLOWED = [
    'application/pdf', 'image/jpeg', 'image/png',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const [attOpen, setAttOpen] = useState(false);
  const [attBill, setAttBill] = useState<Bill | null>(null);
  const [atts, setAtts] = useState<svc.Attachment[]>([]);
  const [attBusy, setAttBusy] = useState(false);
  const attCanAdd = !!attBill && attBill.status !== 'void' && canUpdate;
  const attCanDel = !!attBill && attBill.status === 'draft' && canUpdate;

  const loadAtts = useCallback(async (billId: number) => {
    try { setAtts(await svc.listAttachments(billId)); } catch (err) { showApiError(err); }
  }, [showApiError]);
  const openAtts = (b: Bill) => { setAttBill(b); setAtts([]); setAttOpen(true); loadAtts(b.id); };

  const fmtSize = (n: number) =>
    n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

  const onPickAtt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';                               // 重置以便重選同檔
    if (!file || !attBill) return;
    if (atts.length >= ATT_MAX) { showError('ERR450012'); return; }
    if (file.size > ATT_MAX_BYTES) { showError('ERR450011'); return; }
    if (file.type && !ATT_ALLOWED.includes(file.type)) { showError('ERR450010'); return; }
    setAttBusy(true);
    try {
      await svc.uploadAttachment(attBill.id, file);
      showSuccess('SYS450008');
      await loadAtts(attBill.id);
    } catch (err) { showApiError(err); }
    finally { setAttBusy(false); }
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'attachment';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const downloadAtt = async (a: svc.Attachment) => {
    if (!attBill) return;
    try { triggerDownload(await svc.downloadAttachment(attBill.id, a.id), a.filename); }
    catch (err) { showApiError(err); }
  };
  const deleteAtt = async (a: svc.Attachment) => {
    if (!attBill) return;
    if (!await askConfirm({ title: tt('attDelete', '刪除'),
      message: tt('confirmDeleteAtt', '確定刪除附件？'),
      confirmLabel: t('common.delete', '刪除'), confirmColor: 'error' })) return;
    try { await svc.deleteAttachment(attBill.id, a.id); await loadAtts(attBill.id); }
    catch (err) { showApiError(err); }
  };

  // 帶入用量：年月以對話框選取（取代 window.prompt·S4）。mode: ai AI 用量 / einvoice 電子發票模組服務費
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageMode, setUsageMode] = useState<'ai' | 'einvoice'>('ai');
  const [usageYm, setUsageYm] = useState({
    year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const openUsage = (mode: 'ai' | 'einvoice' = 'ai') => {
    const now = new Date();
    setUsageMode(mode);
    setUsageYm({ year: now.getFullYear(), month: now.getMonth() + 1 });
    setUsageOpen(true);
  };
  const confirmUsage = async () => {
    const { year, month } = usageYm;
    if (!year || !month) return;
    try {
      if (usageMode === 'einvoice') {
        const usage = await einvSvc.computeUsage(draft.tenant_org_id, year, month);
        if (!usage.enabled || usage.items.length === 0) {
          showInfo('SYS450015');   // 該租戶當月未啟用電子發票模組，略過帶入（S7）
          return;
        }
        // 帶入行保留 kind / promo_incl（建議優惠價用）；交由後端套建議優惠價
        const lines = usage.items.map(i => ({
          name: i.name, amount: i.amount, kind: i.kind, promo_incl: i.promo_incl ?? null }));
        setDraft(d => ({ ...d, items: [...d.items, ...lines], promo_manual: false }));
      } else {
        const line = await svc.computeUsage(draft.tenant_org_id, year, month);
        setDraft(d => ({ ...d, items: [...d.items, { name: line.name, amount: line.amount }] }));
      }
      setUsageOpen(false);
    } catch (err) { showApiError(err); }
  };

  const saveDraft = async () => {
    const payload = { ...draft,
      items: draft.items.filter(i => i.name.trim() || i.amount),
      period_start: draft.period_start || undefined, period_end: draft.period_end || undefined,
      due_date: draft.due_date || undefined,
      promo_total: draft.promo_manual ? (draft.promo_total ?? null) : null,
      promo_manual: !!draft.promo_manual };
    try {
      if (editing) await svc.updateBill(editing.id, payload);
      else await svc.createBill(payload);
      showSuccess('SYS450005');
      setEditOpen(false);
      loadBills();
    } catch (err) { showApiError(err); }
  };

  const delDraft = async (b: Bill) => {
    if (!await askConfirm({ title: tt('confirmDeleteDraftTitle', '刪除草稿'),
      message: tt('confirmDeleteDraft', '確定刪除這張草稿？'),
      confirmLabel: t('common.delete', '刪除'), confirmColor: 'error' })) return;
    try { await svc.deleteBill(b.id); showSuccess('SYS020003', { name: b.title }); loadBills(); }
    catch (err) { showApiError(err); }
  };

  // ── 開立並通知 ────────────────────────────────────────
  const issue = async (b: Bill) => {
    try {
      const p = await svc.getRecipientsPreview(b.id);
      let msg = tt('issueConfirmV2',
        `將通知 ${p.admins} 位帳務通知人員（email ${p.email} 封、LINE ${p.line} 則），附件 ${p.attachments} 個，確定開立？`,
        { n: p.admins, email: p.email, line: p.line, att: p.attachments });
      // S5：開關開啟、有統編、非人工發票、金額 > 0 → 預告將開立電子發票
      if (einvEnabled && !b.invoice_no && b.amount > 0) {
        try {
          const prof = await svc.getTenantInvoiceProfile(b.tenant_org_id);
          if (prof.tax_id) {
            msg += '\n' + tt('issueEinvoiceLine',
              `將開立電子發票給 ${prof.invoice_title || prof.org_name || ''}（統編 ${prof.tax_id}），金額 ${b.amount.toLocaleString()}`,
              { title: prof.invoice_title || prof.org_name || '', tax_id: prof.tax_id,
                total: b.amount.toLocaleString() });
          }
        } catch { /* silent ok: 發票預告讀取失敗不擋開立 */ }
      }
      if (!await askConfirm({ title: tt('issue', '開立並通知'), message: msg })) return;
      const r = await svc.issueBill(b.id);
      showSuccess('SYS450001',
        { bill_no: r.bill.bill_no || '', email: String(r.email_sent), line: String(r.line_sent) });
      setNote(buildNote(r));
      loadBills(); loadMeta();
    } catch (err) { showApiError(err); }
  };

  const buildNote = (r: svc.IssueResult): string | null => {
    const parts: string[] = [];
    if (r.no_admin) parts.push(tt('noRecipientWarning', '該租戶沒有人有「我的租戶帳單」權限，已改寄組織聯絡信箱'));
    if (r.failed > 0) parts.push(tt('failedCount', `有 ${r.failed} 則通知送出失敗，請查看通知紀錄`, { n: r.failed }));
    return parts.length ? parts.join('　') : null;
  };

  // ── 已收款 Dialog ─────────────────────────────────────
  const [payOpen, setPayOpen] = useState(false);
  const [payBill, setPayBill] = useState<Bill | null>(null);
  const [payForm, setPayForm] = useState({ paid_amount: 0, paid_date: '', paid_note: '' });
  const openPay = (b: Bill) => {
    setPayBill(b);
    setPayForm({ paid_amount: b.amount,
      paid_date: new Date().toISOString().slice(0, 10), paid_note: '' });
    setPayOpen(true);
  };
  const submitPay = async () => {
    if (!payBill) return;
    if (payForm.paid_amount !== payBill.amount &&
        !await askConfirm({ title: tt('markPaid', '已收款'),
          message: tt('paidMismatch', '實收金額與帳單金額不同，確定要標記收款？') })) return;
    try {
      const r = await svc.payBill(payBill.id, payForm);
      showSuccess('SYS450002', { bill_no: r.bill.bill_no || '' });
      setNote(buildNote(r));
      setPayOpen(false); loadBills(); loadMeta();
    } catch (err) { showApiError(err); }
  };

  // ── 作廢 Dialog ───────────────────────────────────────
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidBill, setVoidBill] = useState<Bill | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const openVoid = (b: Bill) => { setVoidBill(b); setVoidReason(''); setVoidOpen(true); };
  const submitVoid = async () => {
    if (!voidBill || !voidReason.trim()) return;
    try {
      const r = await svc.voidBill(voidBill.id, voidReason.trim());
      showSuccess('SYS450003', { bill_no: r.bill.bill_no || '' });
      setNote(buildNote(r));
      setVoidOpen(false); loadBills(); loadMeta();
    } catch (err) { showApiError(err); }
  };

  const renotify = async (b: Bill) => {
    if (!await askConfirm({ title: tt('renotify', '重新通知'),
      message: tt('renotifyConfirm', '確定重新發送這張帳單的通知？') })) return;
    try {
      const r = await svc.renotifyBill(b.id);
      showSuccess('SYS450004', { bill_no: r.bill.bill_no || '' });
      setNote(buildNote(r)); loadBills(); loadMeta();
    } catch (err) { showApiError(err); }
  };

  // ── 通知紀錄 Dialog ───────────────────────────────────
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [notices, setNotices] = useState<svc.NoticeLog[]>([]);
  const openNotices = async (b: Bill) => {
    try { setNotices(await svc.getBillNotices(b.id)); setNoticesOpen(true); }
    catch (err) { showApiError(err); }
  };

  // 系統電子發票上傳狀態（S5·發票欄顯示）
  const UPLOAD_LABEL: Record<string, { label: string; color: 'warning' | 'success' | 'error' }> = {
    pending: { label: tt('uploadPending', '待上傳'), color: 'warning' },
    uploaded: { label: tt('uploadDone', '已上傳'), color: 'success' },
    failed: { label: tt('uploadFailed', '上傳失敗'), color: 'error' },
  };

  const statusLabel = (s: BillStatus) =>
    tt(`status${s.charAt(0).toUpperCase()}${s.slice(1)}`, s);
  const statusColor = (s: BillStatus): 'default' | 'warning' | 'success' | 'error' =>
    s === 'unpaid' ? 'warning' : s === 'paid' ? 'success' : s === 'void' ? 'error' : 'default';

  // 通知紀錄：代碼 → 語系文字（對不到顯示原值·F2）
  const noticeKindLabel = (k: string) => tt(`noticeKinds.${k}`, k);
  const noticeChannelLabel = (c: string) => tt(`noticeChannels.${c}`, c);
  const noticeStatusLabel = (s: string) => tt(`noticeStatuses.${s}`, s);
  const NOTICE_ERR_KEY: Record<string, string> = {
    'no LINE binding': 'noticeErrNoLineBinding',
    'no email': 'noticeErrNoEmail',
    'platform OA not configured': 'noticeErrOaNotConfigured',
  };
  const noticeErrorLabel = (e?: string | null) => {
    if (!e) return '';
    const key = NOTICE_ERR_KEY[e];
    return key ? tt(key, e) : e;   // 其他錯誤（如寄信伺服器錯誤）照原文
  };
  const noticeStatusColor = (s: string): 'success' | 'error' | 'default' =>
    s === 'sent' ? 'success' : s === 'failed' ? 'error' : 'default';

  if (permLoading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }
  if (!hasPermission(FUNC_CODE, 'read')) {
    return <Box p={3}><Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC_CODE} />

      <Tabs value={topTab} onChange={(_e, v) => setTopTab(v)} sx={{ mt: 1, mb: 1 }}>
        <Tab label={tt('tabBilling', '帳單管理')} />
        <Tab label={tt('tabEinvoice', '電子發票')} />
      </Tabs>

      {topTab === 1 && <EInvoicePanel />}

      {topTab === 0 && (<>
      {/* 匯款帳號 */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>{tt('remit', '匯款帳號')}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField size="small" label={tt('bankName', '銀行')} value={remit.bank_name}
            disabled={!canUpdate} onChange={e => setRemit({ ...remit, bank_name: e.target.value })} />
          <TextField size="small" label={tt('bankCode', '銀行代碼')} value={remit.bank_code}
            disabled={!canUpdate} onChange={e => setRemit({ ...remit, bank_code: e.target.value })} />
          <TextField size="small" label={tt('branch', '分行')} value={remit.branch}
            disabled={!canUpdate} onChange={e => setRemit({ ...remit, branch: e.target.value })} />
          <TextField size="small" label={tt('accountName', '戶名')} value={remit.account_name}
            disabled={!canUpdate} onChange={e => setRemit({ ...remit, account_name: e.target.value })} />
          <TextField size="small" label={tt('accountNo', '帳號')} value={remit.account_no}
            disabled={!canUpdate} onChange={e => setRemit({ ...remit, account_no: e.target.value })} />
          {canUpdate &&
            <Button variant="outlined" onClick={saveRemit}>{tt('saveRemit', '儲存匯款帳號')}</Button>}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {tt('lineUsageMonth', `本月已送出 LINE 訊息 ${lineCount} 則`, { n: lineCount })}
        </Typography>
      </Paper>

      {note && <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setNote(null)}>{note}</Alert>}

      {/* 篩選 + 新增 */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, mb: 1 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" select label={tt('tenant', '租戶')} sx={{ minWidth: 160 }}
          value={fTenant} onChange={e => setFTenant(e.target.value ? Number(e.target.value) : '')}>
          <MenuItem value="">{tt('all', '全部')}</MenuItem>
          {orgs.map(o => <MenuItem key={o.id} value={o.id}>{o.org_name}</MenuItem>)}
        </TextField>
        <TextField size="small" select label={tt('status', '狀態')} sx={{ minWidth: 120 }}
          value={fStatus} onChange={e => setFStatus(e.target.value as BillStatus | '')}>
          <MenuItem value="">{tt('all', '全部')}</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={loadBills}>{t('common.search', '搜尋')}</Button>
        <Box flexGrow={1} />
        {canUpdate && (
          <Badge badgeContent={taxUpdates.length} color="warning"
            title={tt('taxProfileUpdatedBadge', '有租戶更新了公司稅務資料')}>
            <Button variant="outlined" onClick={openProfile}>{tt('taxProfile', '公司稅務資料')}</Button>
          </Badge>
        )}
        {canCreate && <Button variant="contained" onClick={openNew}>{tt('newBill', '新增帳單')}</Button>}
      </Stack>

      {loading ? <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{tt('billNo', '帳單編號')}</TableCell>
              <TableCell>{tt('tenant', '租戶')}</TableCell>
              <TableCell>{tt('billTitle', '標題')}</TableCell>
              <TableCell align="right">{tt('amount', '金額')}</TableCell>
              <TableCell>{tt('dueDate', '繳費期限')}</TableCell>
              <TableCell>{tt('status', '狀態')}</TableCell>
              <TableCell>{tt('invoice', '發票')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.length === 0 ? (
              <TableRow><TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  {tt('noBills', '尚無帳單')}</Typography></TableCell></TableRow>
            ) : bills.map(b => (
              <TableRow key={b.id} hover>
                <TableCell>{b.bill_no || '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{b.tenant_org_name || orgName(b.tenant_org_id)}</span>
                    {updatedOrgIds.has(b.tenant_org_id) && (
                      <Chip size="small" color="warning" variant="outlined"
                        label={tt('taxProfileUpdatedTag', '稅務資料已更新')} />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{b.title}</TableCell>
                <TableCell align="right">NT$ {b.amount.toLocaleString()}</TableCell>
                <TableCell>{b.due_date || '—'}</TableCell>
                <TableCell><Chip size="small" color={statusColor(b.status)} label={statusLabel(b.status)} /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{b.invoice_no || '—'}</span>
                    {b.einvoice_upload_status && UPLOAD_LABEL[b.einvoice_upload_status] && (
                      <Chip size="small" variant="outlined"
                        color={UPLOAD_LABEL[b.einvoice_upload_status].color}
                        label={UPLOAD_LABEL[b.einvoice_upload_status].label}
                        title={b.einvoice_upload_error || undefined} />
                    )}
                    {b.einvoice_status === 'void' &&
                      <Chip size="small" color="default" label={tt('einvoiceVoided', '發票已作廢')} />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    {b.status === 'draft' && canUpdate &&
                      <Button size="small" onClick={() => openEdit(b)}>{t('common.edit', '修改')}</Button>}
                    {b.status === 'draft' && canDelete &&
                      <Button size="small" color="error" onClick={() => delDraft(b)}>{t('common.delete', '刪除')}</Button>}
                    {b.status === 'draft' && canUpdate &&
                      <Button size="small" variant="contained" onClick={() => issue(b)}>{tt('issue', '開立並通知')}</Button>}
                    {b.status === 'unpaid' && canUpdate &&
                      <Button size="small" variant="contained" color="success" onClick={() => openPay(b)}>{tt('markPaid', '已收款')}</Button>}
                    {(b.status === 'unpaid' || b.status === 'paid') && canUpdate &&
                      <Button size="small" color="error" onClick={() => openVoid(b)}>{tt('void', '作廢')}</Button>}
                    {b.status !== 'draft' && canUpdate &&
                      <Button size="small" onClick={() => renotify(b)}>{tt('renotify', '重新通知')}</Button>}
                    {b.status !== 'draft' &&
                      <Button size="small" onClick={() => openNotices(b)}>{tt('viewNotices', '通知紀錄')}</Button>}
                    <Button size="small" onClick={() => openAtts(b)}>{tt('attachments', '附件')}</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </>)}

      {/* 草稿 Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t('common.edit', '修改') : tt('newBill', '新增帳單')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={tt('tenant', '租戶')} value={draft.tenant_org_id} disabled={!!editing}
              onChange={e => setDraft({ ...draft, tenant_org_id: Number(e.target.value) })}>
              {orgs.map(o => <MenuItem key={o.id} value={o.id}>{o.org_name}</MenuItem>)}
            </TextField>
            <TextField label={tt('billTitle', '標題')} value={draft.title}
              onChange={e => setDraft({ ...draft, title: e.target.value })} />
            <Stack direction="row" spacing={1}>
              <TextField fullWidth type="date" label={tt('periodStart', '期間起')}
                InputLabelProps={{ shrink: true }} value={draft.period_start || ''}
                onChange={e => setDraft({ ...draft, period_start: e.target.value })} />
              <TextField fullWidth type="date" label={tt('periodEnd', '期間訖')}
                InputLabelProps={{ shrink: true }} value={draft.period_end || ''}
                onChange={e => setDraft({ ...draft, period_end: e.target.value })} />
            </Stack>
            <Divider textAlign="left"><Typography variant="caption">{tt('items', '明細')}</Typography></Divider>
            {draft.items.map((it, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <TextField fullWidth size="small" label={tt('itemName', '項目')} value={it.name}
                  onChange={e => setItem(idx, { name: e.target.value })} />
                <TextField size="small" type="number" label={tt('itemAmount', '金額')} value={it.amount}
                  sx={{ width: 120 }} onChange={e => setItem(idx, { amount: Number(e.target.value) || 0 })} />
                <IconButton size="small" onClick={() => removeItem(idx)}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" startIcon={<AddIcon />} onClick={addItem}>{tt('addItem', '新增明細')}</Button>
              <Button size="small" onClick={() => openUsage('ai')} disabled={!draft.tenant_org_id}>{tt('importUsage', '帶入 AI 用量')}</Button>
              <Button size="small" onClick={() => openUsage('einvoice')} disabled={!draft.tenant_org_id}>{tt('importEinvoice', '帶入電子發票模組')}</Button>
            </Stack>

            {/* 合計區（稅基模型·S6/B2）：費用小計 → 營業稅 5% → 含稅合計 → 優惠價 → 應繳總額 */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={0.5}>
                <Row label={tt('subtotal', '費用小計（未稅）')} value={draftBreakdown.subtotal} />
                <Row label={tt('taxBefore', '營業稅 5%')} value={draftBreakdown.taxBefore} />
                <Row label={tt('gross', '含稅合計')} value={draftBreakdown.gross} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" type="number" label={tt('promoTotal', '優惠價（含稅總額，選填）')}
                    sx={{ width: 220 }}
                    value={draft.promo_manual ? (draft.promo_total ?? '') : (draftBreakdown.promo ?? '')}
                    onChange={e => {
                      const v = e.target.value;
                      setDraft(d => ({ ...d, promo_manual: true,
                        promo_total: v === '' ? null : Number(v) }));
                    }}
                    helperText={tt('promoHint', `留空＝依含稅合計；建議 ${draftBreakdown.suggested.toLocaleString()}`,
                      { suggested: draftBreakdown.suggested.toLocaleString() })} />
                  {draft.promo_manual && (
                    <Button size="small" onClick={() =>
                      setDraft(d => ({ ...d, promo_manual: false, promo_total: null }))}>
                      {tt('promoReset', '恢復建議值')}</Button>
                  )}
                </Stack>
                {draftBreakdown.promo != null && (
                  <Typography variant="caption" color="text.secondary">
                    {tt('promoSplit', `其中 銷售額 ${draftBreakdown.sales.toLocaleString()}、稅額 ${draftBreakdown.tax.toLocaleString()}`,
                      { sales: draftBreakdown.sales.toLocaleString(), tax: draftBreakdown.tax.toLocaleString() })}
                  </Typography>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={600}>{tt('amountDue', '應繳總額')}</Typography>
                  <Typography fontWeight={600}>NT$ {draftTotal.toLocaleString()}</Typography>
                </Stack>
              </Stack>
            </Paper>
            <TextField type="date" label={tt('dueDate', '繳費期限')} InputLabelProps={{ shrink: true }}
              value={draft.due_date || ''} onChange={e => setDraft({ ...draft, due_date: e.target.value })} />
            <TextField label={tt('invoiceNo', '發票號碼')} value={draft.invoice_no || ''}
              onChange={e => setDraft({ ...draft, invoice_no: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={saveDraft}
            disabled={!draft.tenant_org_id || !draft.title.trim()}>{tt('saveDraft', '儲存草稿')}</Button>
        </DialogActions>
      </Dialog>

      {/* 已收款 Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('markPaid', '已收款')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField type="number" label={tt('paidAmount', '實收金額')} value={payForm.paid_amount}
              onChange={e => setPayForm({ ...payForm, paid_amount: Number(e.target.value) || 0 })} />
            <TextField type="date" label={tt('paidDate', '收款日期')} InputLabelProps={{ shrink: true }}
              value={payForm.paid_date} onChange={e => setPayForm({ ...payForm, paid_date: e.target.value })} />
            <TextField label={tt('paidNote', '備註（匯款帳號後 5 碼）')} value={payForm.paid_note}
              onChange={e => setPayForm({ ...payForm, paid_note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={submitPay}>{t('common.confirm', '確定')}</Button>
        </DialogActions>
      </Dialog>

      {/* 作廢 Dialog */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('void', '作廢')}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} sx={{ mt: 1 }} label={tt('voidReason', '作廢原因')}
            value={voidReason} onChange={e => setVoidReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color="error" onClick={submitVoid}
            disabled={!voidReason.trim()}>{t('common.confirm', '確定')}</Button>
        </DialogActions>
      </Dialog>

      {/* 通知紀錄 Dialog */}
      <Dialog open={noticesOpen} onClose={() => setNoticesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{tt('viewNotices', '通知紀錄')}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tt('noticeKind', '類型')}</TableCell>
                <TableCell>{tt('noticeChannel', '管道')}</TableCell>
                <TableCell>{tt('noticeStatus', '狀態')}</TableCell>
                <TableCell>{tt('noticeError', '錯誤')}</TableCell>
                <TableCell>{tt('noticeSentAt', '時間')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.length === 0 ? (
                <TableRow><TableCell colSpan={5}>{tt('noNotices', '尚無通知紀錄')}</TableCell></TableRow>
              ) : notices.map(n => (
                <TableRow key={n.id}>
                  <TableCell>{noticeKindLabel(n.kind)}</TableCell>
                  <TableCell>{noticeChannelLabel(n.channel)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={noticeStatusLabel(n.status)}
                      color={noticeStatusColor(n.status)} />
                  </TableCell>
                  <TableCell>{noticeErrorLabel(n.error)}</TableCell>
                  <TableCell>{n.sent_at ? new Date(n.sent_at).toLocaleString() : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setNoticesOpen(false)}>{t('common.close', '關閉')}</Button></DialogActions>
      </Dialog>

      {/* 附件 Dialog（v2）*/}
      <Dialog open={attOpen} onClose={() => setAttOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tt('attachments', '附件')}
          {attBill ? `　${attBill.bill_no || attBill.title}` : ''}
        </DialogTitle>
        <DialogContent>
          {attCanAdd && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
              <Button variant="outlined" component="label" startIcon={<AddIcon />} disabled={attBusy}>
                {tt('uploadAttachment', '上傳附件')}
                <input hidden type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx" onChange={onPickAtt} />
              </Button>
              {attBusy && <CircularProgress size={20} />}
              <Typography variant="caption" color="text.secondary">
                {tt('attachmentHint', 'PDF / JPG / PNG / XLSX / DOCX；單檔 5MB、合計 10MB、最多 5 個')}
              </Typography>
            </Stack>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tt('attName', '檔名')}</TableCell>
                <TableCell align="right">{tt('attSize', '大小')}</TableCell>
                <TableCell>{tt('attUploadedAt', '時間')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {atts.length === 0 ? (
                <TableRow><TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    {tt('noAttachments', '尚無附件')}</Typography></TableCell></TableRow>
              ) : atts.map(a => (
                <TableRow key={a.id}>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{a.filename}</TableCell>
                  <TableCell align="right">{fmtSize(a.size_bytes)}</TableCell>
                  <TableCell>{a.uploaded_at ? new Date(a.uploaded_at).toLocaleString() : ''}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => downloadAtt(a)}>{tt('attDownload', '下載')}</Button>
                    {attCanDel &&
                      <Button size="small" color="error" onClick={() => deleteAtt(a)}>{tt('attDelete', '刪除')}</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setAttOpen(false)}>{t('common.close', '關閉')}</Button></DialogActions>
      </Dialog>

      {/* 帶入用量：年月選取 Dialog（AI 用量 / 電子發票模組服務費）*/}
      <Dialog open={usageOpen} onClose={() => setUsageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{usageMode === 'einvoice'
          ? tt('importEinvoice', '帶入電子發票模組') : tt('importUsage', '帶入 AI 用量')}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField fullWidth type="number" label={tt('usageYear', '年')} value={usageYm.year}
              onChange={e => setUsageYm(s => ({ ...s, year: Number(e.target.value) || 0 }))} />
            <TextField fullWidth select label={tt('usageMonth', '月')} value={usageYm.month}
              onChange={e => setUsageYm(s => ({ ...s, month: Number(e.target.value) }))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
                <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {usageMode === 'einvoice'
              ? tt('einvoiceUsageHint', '帶入電子發票模組月費（未稅）；信箱數達門檻將自動帶入折抵。租戶當月未啟用則不帶入。')
              : tt('usageHint', 'AI 用量以美元計價，非台幣時僅帶入名稱，請換算新台幣後填入金額')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageOpen(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={confirmUsage}
            disabled={!usageYm.year || !usageYm.month}>{t('common.confirm', '確定')}</Button>
        </DialogActions>
      </Dialog>

      {/* 公司稅務資料 Dialog（統編 / 公司登記名稱 / 稅籍編號·租戶 + 平台共維）*/}
      <Dialog open={profOpen} onClose={() => setProfOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('taxProfile', '公司稅務資料')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={tt('tenant', '租戶')} value={profOrg}
              onChange={e => { const id = Number(e.target.value); setProfOrg(id); loadProfile(id); }}>
              {orgs.map(o => (
                <MenuItem key={o.id} value={o.id}>
                  {o.org_name}{updatedOrgIds.has(o.id) ? tt('taxProfileUpdatedSuffix', '（已更新）') : ''}
                </MenuItem>
              ))}
            </TextField>
            {profLoaded?.tenant_updated_unseen && (
              <Alert severity="info">{tt('taxProfileTenantUpdated', '此租戶稅務資料已由租戶更新，請確認')}</Alert>
            )}
            <TextField label={tt('taxId', '統一編號（8 碼）')} value={prof.tax_id}
              inputProps={{ maxLength: 8 }}
              onChange={e => setProf(p => ({ ...p, tax_id: e.target.value }))} />
            <TextField label={tt('companyRegName', '公司登記名稱')} value={prof.invoice_title}
              inputProps={{ maxLength: 200 }}
              onChange={e => setProf(p => ({ ...p, invoice_title: e.target.value }))} />
            <TextField label={tt('taxRegistrationNo', '稅籍編號')} value={prof.tax_registration_no}
              inputProps={{ maxLength: 9 }}
              helperText={tt('taxRegistrationNoHint', '國稅局核發·9 碼數字（選填）')}
              onChange={e => setProf(p => ({ ...p, tax_registration_no: e.target.value }))} />
            <Typography variant="caption" color="text.secondary">
              {tt('taxIdHint', '開立電子發票前，買方（租戶）統編必填且需通過檢查碼驗證。')}
            </Typography>
            {profLoaded?.updated_at && (
              <Typography variant="caption" color="text.secondary">
                {tt('taxProfileUpdatedAt', '最後修改')}：{new Date(profLoaded.updated_at).toLocaleString()}
                {' '}{tt('by', '由')}
                {profLoaded.updated_side === 'tenant'
                  ? tt('sideTenantForPlatform', '租戶') : tt('sidePlatform', '平台')}
                {profLoaded.updated_by_name ? ` ${profLoaded.updated_by_name}` : ''}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfOpen(false)}>{t('common.close', '關閉')}</Button>
          <Button variant="contained" onClick={saveProfile} disabled={profBusy || !profOrg}>
            {t('common.save', '儲存')}</Button>
        </DialogActions>
      </Dialog>

      {/* 共用確認 Dialog（取代 window.confirm·S4）*/}
      <Dialog open={confirmState.open} onClose={() => closeConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmState.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, whiteSpace: 'pre-line' }}>{confirmState.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirm(false)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" color={confirmState.confirmColor}
            onClick={() => closeConfirm(true)}>{confirmState.confirmLabel}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformBillingPage;
